<template>
  <div class="jobs-table">

    <!-- Filters -->
    <div class="card p-2 pt-3 bg-light border rounded-0">
      
      <b-row>
        <!-- Left Column -->
        <b-col class="my-1" xl="6" lg="6" md="12" >
          
          <!-- Accounts -->
          <b-form-group
            v-if="userAccounts.length > 1"
            label="Account"
            label-for=""
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <multiselect 
              v-model="table_filters.account"
              trackBy="account"
              label="name"
              size="sm"
              :options="userAccounts"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              placeholder="Select to filter..."
              @input="table_filters.currentPage=1; refreshTableData()"
            ></multiselect>
          </b-form-group>

          <!-- From date -->
          <b-form-group
            label="From"
            label-for=""
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-form-datepicker size="sm" class="border-radius-none" v-model="table_filters.from_date" start-weekday="1" min="2016-01-01" :reset-button="true" locale="en" placeholder="Select to limit" @input="table_filters.currentPage=1; refreshTableData()"></b-form-datepicker>
          </b-form-group>

          <!-- To date -->
          <b-form-group
            label="To"
            label-for=""
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-form-datepicker size="sm" class="border-radius-none" v-model="table_filters.to_date" start-weekday="1" min="2016-01-01" :reset-button="true" locale="en" placeholder="Select to limit" @input="table_filters.currentPage=1; refreshTableData()"></b-form-datepicker>
          </b-form-group>
        </b-col>
        
        <!-- Right Column -->
        <b-col class="my-1 align-self-end" xl="6" lg="6" md="12">

          <!-- <CountryTags /> -->

          <b-form-group
            label="Country"
            label-for="country-select"
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-form-select
              id="country-select"
              v-model="table_filters.country"
              size="sm"
              :options="availableDeliveryCountries"
              value-field="id"
              text-field="name"
              @change="table_filters.currentPage=1; refreshTableData()"
            >
              <template #first>
                <b-form-select-option :value="null">Select to filter...</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>

          <!-- Status -->
          <b-form-group
            label="Status"
            label-for=""
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-select
              :options="table_status_filters"
              v-model="table_filters.status" size="sm"
              value-field="id"
              text-field="text"
              @change="table_filters.currentPage=1; refreshTableData()"
            />
          </b-form-group>

          <!-- Search -->
          <b-form-group
            label="Search"
            label-for=""
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-input-group class="ml-auto">
              <b-input v-model="table_filters.search" size="sm" placeholder="Filter table..." debounce="400" v-on:update="table_filters.currentPage=1; refreshTableData(true)"/>
              <b-input-group-append>
                <b-button size="sm" v-on:click="refreshTableData(true)"><b-icon icon="arrow-clockwise"></b-icon></b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

        </b-col>
      </b-row>
      
    </div>

    <!-- Job Table -->
    <b-table
      ref="table"
      responsive
      show-empty
      emptyText="There are no jobs to show"

      :hover="false"
      :outlined="false"
      :borderless="false"

      primary-key="id"
      :items="tabledataProvider"
      :fields="table_fields"
      :tbody-tr-class="rowClass"

      :per-page="table_filters.perPage"
      :current-page="table_filters.currentPage"

      :busy.sync="table_settings.isBusy"
      :no-local-sorting="true"
      :sort-by.sync="table_filters.sortBy"
      :sort-desc.sync="table_filters.sortDesc"
    >
      <!-- custom formatted columns -->
      <template #cell(jobid)="data">
        <b-link :to="{name:'JobDetails', params: { job_id: data.item.id }}">{{data.item.jobid}}</b-link>
      </template>

      <template #cell(created_at)="data">
        {{ data.item.created_at | moment("Y-MM-DD HH:mm") }}
      </template>

      <template #cell(account)="data">
        {{ userAccountName(data.item.account) }}
      </template>

      <template #cell(country)="data">
        <abbr :title="countryByCode(data.item.country)">
          {{ data.item.country || "Unknown" }}
        </abbr>
        <!-- <span v-html="flags(1)"></span> -->
      </template>

      <template #cell(status)="data">
        {{ printerapiStatusName(data.item.status) }}
        <b-icon v-if="data.item.warnings_active_public" 
          icon="exclamation-diamond" 
          class="text-danger ml-2"
          v-b-tooltip.hover.left.html="data.item.fulldata_warnings.map(x => x.text).join(',<br>')"
        ></b-icon>

        <b-icon v-if="data.item.flags && data.item.flags.includes('+replace')" 
          icon="arrow-down-right-circle-fill" 
          class="text-warning ml-2"
          v-b-tooltip.hover.left="`Order is unlocked and may be replaced by your integration.`"
        ></b-icon>

      </template>

      <!-- Row Details Toggle --
      <template #cell(actions)="row">
        <b-button size="sm" @click="row.toggleDetails" class="">
          <b-icon icon="chevron-down" v-if="!row.detailsShowing"></b-icon>
          <b-icon icon="chevron-up" v-else></b-icon>
        </b-button>
      </template>-->

      <!-- Row Details --
      <template #row-details="row">
        <b-card>
          <b-row>
            <b-col sm="3" class="text-sm-right"><b>Warnings:</b></b-col>
            <b-col><div v-for="(warning, wkey) in row.item.fulldata_warnings" :key="`${row.item.id}-warning-${wkey}`">{{ warning.text }}</div></b-col>
          </b-row>
          <b-row>
            <b-col sm="3" class="text-sm-right"><b>Is Active:</b></b-col>
            <b-col>{{ row.item.isActive }}</b-col>
          </b-row>
        </b-card>
      </template>-->

    </b-table>

    <!-- table pagination -->
    <!--
    <b-row>
      <b-col col="12" class="text-right">
        <b-pagination
          class="d-inline-flex"
          v-model="table_filters.currentPage"
          :total-rows="table_settings.total_rows"
          :per-page="table_filters.perPage"
        >
        </b-pagination>
      </b-col>
    </b-row>
    -->

      <b-row>
        <b-col sm="6" md="6" lg="4" class="my-1">
          <b-form-group
            label="Per page"
            label-for="per-page-select"
            label-cols-sm="3"
            label-align-sm="right"
            label-size="sm"
            class="mb-1"
          >
            <b-form-select
              id="per-page-select"
              v-model="table_filters.perPage"
              size="sm"
              :options="table_settings.pageOptions"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <b-col sm="6" md="6" lg="4" offset-sm="0" offset-md="0" offset-lg="4" class="my-1">
          <b-pagination
            v-model="table_filters.currentPage"
            :total-rows="table_settings.total_rows"
            :per-page="table_filters.perPage"
            align="fill"
            size="sm"
          >
          </b-pagination>
        </b-col>
      </b-row>
    
  </div>
</template>


<script>
import { mapActions, mapGetters } from 'vuex';
//import Multiselect from '@/components/JobContent/Multiselect.vue';
//import CountryTags from '@/components/Tags/CountryTags.vue';

export default {
  name: 'JobTable',
  components: {
    //Multiselect,
    //CountryTags,
  },

  props: {
    settings: {
      type: null,
      default: function () {
        return {}
      }
    },
    filters: {
      type: null,
      default: function () {
        return {}
      }
    }
  },

  data() {
    return {
      
      table_settings: {
        isBusy: false,
        total_rows: 9999,
        pageOptions: [10, 15, 30], // { value: 100, text: "Show a lot" }
      },
      table_filters: { // Specify also here to make it responsive...
        currentPage: 1,
        perPage: 10,
        sortBy: 'created_at',
        sortDesc: true,
        search: '', // - since we wrote our own
        status: null,
        account: null,
        country: null,
        from_date: this.defaultFromDate(),
      },
      // Table columns
      table_fields_template: [
        {
          key: 'jobid',
          label: 'Job Id',
          sortable: true,
          //tdClass:"text-primary cursor-pointer",  // stickyColumn: true, isRowHeader: true, variant: 'info',
        },
        {
          access: ['superadmin'],
          key: 'account',
          label: 'Account',
          sortable: false,
          //tdClass:"text-primary cursor-pointer",  // stickyColumn: true, isRowHeader: true, variant: 'info',
        },
        {
          key: 'created_at',
          label: 'Created',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          thClass: 'd-none d-md-table-cell',
          tdClass: 'd-none d-md-table-cell'
        },
        {
          key: 'country',
          label: 'Country',
          sortable: true,
          thClass: 'd-none d-md-table-cell',
          tdClass: 'd-none d-md-table-cell'
        },
        /*
        {
          key: 'actions',
          label: '',
        },
        */
      ],

      // Filter: Status
      table_status_filters: [
        {id: null, text: 'Select to filter...' },
        {id: 'incoming', text: 'Incoming'},
        {id: 'in-production', text: 'In production'},
        {id: 'cancelled', text: 'Cancelled'},
        {id: 'failed', text: 'Failed'},
        {id: 'completed', text: 'Completed'},
        {id: 'warnings', text: 'Warnings'}
      ],
    }
  },

  async created() {
    let filters = this.getPageSettings( this.$route.name ) // from saved settings
    this.table_settings = Object.assign({}, this.table_settings, this.settings)
    this.table_filters = Object.assign({}, this.table_filters, filters, this.filters) // default <- saved <- props
    //await this.tabledataProvider();
    
    this.savePageSettings({id: this.$route.name, settings: this.table_filters }); // Save it back to update from incoming forced args
  },

  computed: {
    ...mapGetters([
      'getPageSettings',
      'timestampAsDate',
      'availableDeliveryCountries',
      'countryByCode',
    ]),
    ...mapGetters('user', [
      'currentUser',
      'userAccounts',
      'userAccountName',
      'userHasAnyOfRoles',
    ]),
    ...mapGetters('job', [
      'printerapiStatusName'
    ]),

    table_fields() {
      let fields = this.table_fields_template;
      if ( ! this.userHasAnyOfRoles(['superadmin']) ) {
        fields = fields.filter( x => ! x.access || ! x.access.includes('superadmin') );
      }
      return fields;
    },
  },

  methods: {
    ...mapActions([
      'savePageSettings',
    ]),

    ...mapActions('job', [
      'queryJobsPromise',
    ]),

    refreshTableData() {
      this.$refs.table.refresh()
    },

    defaultFromDate() {
      return this.$moment().subtract(90, 'days').format("YYYY-MM-DD");
    },

    tabledataProvider(ctx){
      const queryObject = {
        'account': this.table_filters.account?.account ?? null, // multiselect does only return full selected object
        'status': this.table_filters.status ?? null,
        'search': this.table_filters.search ?? null,
        'country': this.table_filters.country ?? null,
        'from_date': this.table_filters.from_date ?? null,
        'to_date': this.table_filters.to_date ?? null,
        ...ctx,
      };

      // Save filters, instead of with watcher
      this.savePageSettings({id: this.$route.name, settings: this.table_filters });

      const promise = this.queryJobsPromise(queryObject)
      return promise.then(response => {
        this.table_settings.total_rows = response?.data?.total ?? 0;
        return response?.data?.items ?? [];
      })
    },

    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (item?.warnings_active_public) return 'table-warning'
      if (item?.deleted) return 'table-danger'
    },

    /*
    flags(id) {
      console.log(id)
      return '<svg viewBox="0 0 640 480"><path fill="#066aa7" d="M0 0h640v480H0z"/><path fill="#fecc00" d="M0 192h640v96H0z"/><path fill="#fecc00" d="M176 0h96v480h-96z"/></svg>';
    }
    */
  },
}
</script>

<style lang="scss">
svg { height: 1em; width: auto; }
</style>